import React, { useState, useEffect, useRef, useMemo } from "react";
import { useUser, useAuth, useClerk } from "@clerk/clerk-react";
import { DragDropContext, Droppable, Draggable } from "react-beautiful-dnd";
import {
  ChevronLeft,
  ChevronRight,
  MessageSquare,
  FolderOpen,
  FolderPlus,
  Plus,
  Search,
  Settings,
  Star,
  Edit2,
  Trash2,
  MoreVertical,
  Check,
  X,
  Palette,
  DoorOpen,
  DoorClosed,
  LogOut,
  CheckSquare,
  Square,
  XCircle,
} from "lucide-react";
import { authFetch, authFetchJson, apiUrl } from "../utils/api";
import InstallPrompt from "./InstallPrompt";
import SidebarModeToggle from "./SidebarModeToggle";
import { FileBrowser } from "./FileBrowser";
import CommandConversationNav from "./CommandCenter/CommandConversationNav";
import "./ConversationSidebar.css";

// Cross-surface last-touch badge. conv.last_surface comes from the most
// recent message's `source` (Wave B). One login → every chat labeled by
// which agent last touched it. Web/Ollie is the implicit default → no
// badge (keeps the list clean); only the "away" surfaces get a chip.
const SURFACE_BADGES = {
  mako: { img: "/images/characters/mako.webp", label: "Mako (Telegram)" },
  orca: { img: "/images/characters/orca.webp", label: "Orca (desktop)" },
  harry: { img: "/images/characters/harry.webp", label: "Harry (background)" },
};
function surfaceBadge(source) {
  if (!source) return null;
  return SURFACE_BADGES[String(source).toLowerCase()] || null;
}

// Predefined folder colors
const FOLDER_COLORS = [
  "#007AFF", // Indigo (default)
  "#007AFF", // Purple
  "#ec4899", // Pink
  "#ef4444", // Red
  "#f97316", // Orange
  "#eab308", // Yellow
  "#22c55e", // Green
  "#14b8a6", // Teal
  "#3b82f6", // Blue
  "#64748b", // Slate
];

const ConversationSidebar = ({
  currentConversationId,
  onSelectConversation,
  onNewConversation,
  onConversationDeleted,
  isCollapsed,
  onToggleCollapse,
  onOpenSettings,
  user,
  onLogout,
  sidebarMode = "chats",
  onSidebarModeChange,
  onFileUseInChat,
  commandCenterChatEnabled = false,
}) => {
  // const { user, isLoaded: userLoaded } = useUser(); // User passed as prop now
  const userLoaded = !!user;
  const { getToken, isSignedIn } = useAuth();
  const { openUserProfile } = useClerk();
  const userId = user?.id;
  const [conversations, setConversations] = useState([]);
  const [showMobileUserMenu, setShowMobileUserMenu] = useState(false);
  const [folders, setFolders] = useState([]);
  const [searchQuery, setSearchQuery] = useState("");
  const [expandedFolders, setExpandedFolders] = useState(new Set());
  const [editingId, setEditingId] = useState(null);
  const [editingTitle, setEditingTitle] = useState("");
  const [deleteConfirmId, setDeleteConfirmId] = useState(null);
  const [contextMenuId, setContextMenuId] = useState(null);
  const [searchResults, setSearchResults] = useState(null);
  const editInputRef = useRef(null);
  const searchTimeoutRef = useRef(null);
  const [isLoadingConversations, setIsLoadingConversations] = useState(false);
  const [conversationError, setConversationError] = useState(null);
  const [commandManagementOpen, setCommandManagementOpen] = useState(false);

  // Folder management state
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [editingFolder, setEditingFolder] = useState(null);
  const [folderName, setFolderName] = useState("");
  const [folderColor, setFolderColor] = useState(FOLDER_COLORS[0]);
  const [folderContextMenuId, setFolderContextMenuId] = useState(null);
  const [deleteFolderConfirmId, setDeleteFolderConfirmId] = useState(null);
  const folderNameInputRef = useRef(null);

  // Multi-select state for conversations
  const [selectionMode, setSelectionMode] = useState(false);
  const [selectedConversations, setSelectedConversations] = useState(new Set());
  const [bulkDeleting, setBulkDeleting] = useState(false);

  // Only load conversations when user is authenticated
  useEffect(() => {
    // STEP 1a: Try to load FOLDERS from cache first (must load before conversations)
    const foldersCached = localStorage.getItem("folders_cache");
    if (foldersCached && userId) {
      try {
        const data = JSON.parse(foldersCached);
        // Security: Only use cache if it belongs to current user (5 min TTL)
        if (data.userId === userId && data.timestamp > Date.now() - 300000) {
          console.log("⚡ [LOAD] Using cached folders (fresh)");
          setFolders(data.folders || []);
          setExpandedFolders(new Set((data.folders || []).map((f) => f.id)));
        } else if (data.userId !== userId) {
          console.log("🔒 [SECURITY] Clearing folders cache for different user");
          localStorage.removeItem("folders_cache");
        }
      } catch (e) {
        localStorage.removeItem("folders_cache");
      }
    }

    // STEP 1b: Try to load CONVERSATIONS from cache
    const cached = localStorage.getItem("conversations_cache");
    if (cached && userId) {
      try {
        const data = JSON.parse(cached);

        // Security: Only use cache if it belongs to current user
        // ✅ FIX #3: Increased cache TTL from 60s to 5 minutes (300000ms)
        if (data.userId === userId && data.timestamp > Date.now() - 300000) {
          console.log("⚡ [LOAD] Using cached conversations (fresh)");
          setConversations(data.conversations);
        } else if (data.userId !== userId) {
          // Wrong user - clear cache immediately
          console.log("🔒 [SECURITY] Clearing cache for different user");
          localStorage.removeItem("conversations_cache");
        }
      } catch (e) {
        // Invalid cache - clear it
        localStorage.removeItem("conversations_cache");
      }
    }

    // STEP 2: Auth check still required for fresh data
    if (!userLoaded || !isSignedIn || !userId) {
      console.log("📚 [LOAD] Waiting for auth...", {
        userLoaded,
        isSignedIn,
        userId,
      });
      return;
    }

    // STEP 3: Fetch fresh data in background
    console.log("📚 [LOAD] Auth ready, loading conversations...");
    loadConversations();
    loadFolders();
    // Expose reload function to parent
    window.reloadSidebar = loadConversations;

    // Listen for conversation events
    const handleConversationEvent = (event) => {
      console.log("📢 [EVENT] Received event:", event.type);
      // Add debounce to prevent multiple rapid reloads
      clearTimeout(window.reloadTimeout);
      window.reloadTimeout = setTimeout(() => {
        loadConversations();
      }, 200);
    };

    window.addEventListener("conversationCreated", handleConversationEvent);
    window.addEventListener("conversationUpdated", handleConversationEvent);

    return () => {
      window.removeEventListener(
        "conversationCreated",
        handleConversationEvent,
      );
      window.removeEventListener(
        "conversationUpdated",
        handleConversationEvent,
      );
      clearTimeout(window.reloadTimeout);
    };
  }, [userLoaded, isSignedIn, userId]);

  // Close context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (
        contextMenuId &&
        !e.target.closest(".context-menu") &&
        !e.target.closest(".action-btn")
      ) {
        setContextMenuId(null);
      }
    };

    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [contextMenuId]);

  const runConversationSearch = async (query) => {
    try {
      const res = await authFetch(
        apiUrl(`/api/chat/search?query=${encodeURIComponent(query)}`),
      );
      const data = await res.json();
      if (Array.isArray(data)) {
        setSearchResults(data);
        setConversationError(null);
      }
    } catch (error) {
      console.error("Search failed:", error);
      setSearchResults([]);
      setConversationError("Search couldn’t load. Try again.");
    }
  };

  // Debounced search
  useEffect(() => {
    if (searchTimeoutRef.current) {
      clearTimeout(searchTimeoutRef.current);
    }

    if (searchQuery.trim() === "") {
      setSearchResults(null);
      setConversationError(null);
      return;
    }

    searchTimeoutRef.current = setTimeout(
      () => runConversationSearch(searchQuery),
      300,
    );

    return () => {
      if (searchTimeoutRef.current) {
        clearTimeout(searchTimeoutRef.current);
      }
    };
  }, [searchQuery]);

  const loadConversations = async () => {
    // ✅ FIX #3: Prevent duplicate calls
    if (isLoadingConversations) {
      console.log("⏭️ [LOAD] Already loading, skipping duplicate call");
      return;
    }

    try {
      setIsLoadingConversations(true);
      setConversationError(null);
      console.log("📚 [LOAD] Fetching conversations via authFetchJson...");
      const data = await authFetchJson(apiUrl("/api/conversations"));
      console.log(
        `📚 [LOAD] Received ${Array.isArray(data) ? data.length : 0} conversations`,
      );

      if (Array.isArray(data)) {
        // Filter out any archived conversations (belt and suspenders)
        const nonArchived = data.filter((c) => {
          // Be very explicit about filtering
          const isArchived =
            c.is_archived === 1 ||
            c.is_archived === true ||
            c.is_archived === "1";
          return !isArchived;
        });
        console.log(
          `📚 [LOAD] After filtering archived: ${nonArchived.length} conversations`,
        );

        // Keep the conversations in their saved order
        const sorted = nonArchived.sort((a, b) => {
          // First by display_order if set
          if (a.display_order !== undefined && b.display_order !== undefined) {
            return a.display_order - b.display_order;
          }
          // Then by updated_at
          return new Date(b.updated_at) - new Date(a.updated_at);
        });
        setConversations(sorted);

        // Update cache with fresh data
        if (userId) {
          localStorage.setItem(
            "conversations_cache",
            JSON.stringify({
              conversations: sorted,
              timestamp: Date.now(),
              userId: userId,
            }),
          );
          console.log("💾 [CACHE] Updated cache with fresh conversations");
        }

        return sorted;
      } else {
        console.error("📚 [LOAD] Unexpected response format:", data);
        setConversations([]);
      }
    } catch (error) {
      console.error("📚 [LOAD] Failed to load conversations:", error);
      setConversationError("Commands couldn’t load. Try again.");
      // ✅ FIX #3: Use stale cache as fallback on error
      const cached = localStorage.getItem("conversations_cache");
      if (cached && userId) {
        try {
          const data = JSON.parse(cached);
          if (data.userId === userId) {
            console.log("🔄 [LOAD] Using stale cache as fallback");
            setConversations(data.conversations);
          }
        } catch (cacheError) {
          console.error("Failed to parse cache:", cacheError);
        }
      }
    } finally {
      setIsLoadingConversations(false); // ✅ FIX #3: Always clear loading flag
    }
  };

  const loadFolders = async () => {
    try {
      const data = await authFetchJson(apiUrl("/api/chat/folders"));
      if (data.success) {
        const folderList = data.folders || [];
        setFolders(folderList);
        setExpandedFolders(new Set(folderList.map((f) => f.id)));

        // Cache folders like we cache conversations
        if (userId) {
          localStorage.setItem(
            "folders_cache",
            JSON.stringify({
              folders: folderList,
              timestamp: Date.now(),
              userId: userId,
            }),
          );
          console.log("💾 [CACHE] Updated folders cache");
        }
      }
    } catch (error) {
      console.error("Failed to load folders:", error);
      // Use cached folders as fallback
      const cached = localStorage.getItem("folders_cache");
      if (cached && userId) {
        try {
          const data = JSON.parse(cached);
          if (data.userId === userId) {
            console.log("🔄 [LOAD] Using cached folders as fallback");
            setFolders(data.folders || []);
            setExpandedFolders(new Set((data.folders || []).map((f) => f.id)));
          }
        } catch (cacheError) {
          console.error("Failed to parse folders cache:", cacheError);
        }
      }
    }
  };

  // ============ FOLDER MANAGEMENT FUNCTIONS ============

  // Helper to update folder cache after any change
  const updateFolderCache = (updatedFolders) => {
    if (userId) {
      localStorage.setItem(
        "folders_cache",
        JSON.stringify({
          folders: updatedFolders,
          timestamp: Date.now(),
          userId: userId,
        }),
      );
      console.log("💾 [CACHE] Updated folders cache after change");
    }
  };

  const openCreateFolderModal = () => {
    setEditingFolder(null);
    setFolderName("");
    setFolderColor(FOLDER_COLORS[0]);
    setShowFolderModal(true);
    setTimeout(() => folderNameInputRef.current?.focus(), 100);
  };

  const openEditFolderModal = (folder) => {
    setEditingFolder(folder);
    setFolderName(folder.name);
    setFolderColor(folder.color || FOLDER_COLORS[0]);
    setShowFolderModal(true);
    setFolderContextMenuId(null);
    setTimeout(() => folderNameInputRef.current?.focus(), 100);
  };

  const closeFolderModal = () => {
    setShowFolderModal(false);
    setEditingFolder(null);
    setFolderName("");
    setFolderColor(FOLDER_COLORS[0]);
  };

  const saveFolder = async () => {
    if (!folderName.trim()) {
      alert("Please enter a folder name");
      return;
    }

    try {
      if (editingFolder) {
        // Update existing folder
        const response = await authFetch(
          apiUrl(`/api/chat/folders/${editingFolder.id}`),
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({
              name: folderName.trim(),
              color: folderColor,
            }),
          },
        );

        if (response.ok) {
          const updatedFolders = folders.map((f) =>
            f.id === editingFolder.id
              ? { ...f, name: folderName.trim(), color: folderColor }
              : f,
          );
          setFolders(updatedFolders);
          updateFolderCache(updatedFolders);
          closeFolderModal();
        } else {
          alert("Failed to update folder");
        }
      } else {
        // Create new folder
        const response = await authFetch(apiUrl("/api/chat/folders"), {
          method: "POST",
          headers: { "Content-Type": "application/json" },
          body: JSON.stringify({
            name: folderName.trim(),
            color: folderColor,
          }),
        });

        if (response.ok) {
          const data = await response.json();
          if (data.success && data.folder) {
            const updatedFolders = [...folders, data.folder];
            setFolders(updatedFolders);
            setExpandedFolders((prev) => new Set([...prev, data.folder.id]));
            updateFolderCache(updatedFolders);
          }
          closeFolderModal();
        } else {
          alert("Failed to create folder");
        }
      }
    } catch (error) {
      console.error("Failed to save folder:", error);
      alert("Failed to save folder");
    }
  };

  const deleteFolder = async (folderId) => {
    try {
      const response = await authFetch(
        apiUrl(`/api/chat/folders/${folderId}`),
        {
          method: "DELETE",
        },
      );

      if (response.ok) {
        // Remove folder from state and update cache
        const updatedFolders = folders.filter((f) => f.id !== folderId);
        setFolders(updatedFolders);
        updateFolderCache(updatedFolders);
        // Move conversations from this folder back to recent
        setConversations((prev) =>
          prev.map((c) =>
            c.folder_id === folderId ? { ...c, folder_id: null } : c,
          ),
        );
        setDeleteFolderConfirmId(null);
        setFolderContextMenuId(null);
      } else {
        alert("Failed to delete folder");
      }
    } catch (error) {
      console.error("Failed to delete folder:", error);
      alert("Failed to delete folder");
    }
  };

  // Close folder context menu when clicking outside
  useEffect(() => {
    const handleClickOutside = (e) => {
      if (folderContextMenuId && !e.target.closest(".folder-context-menu")) {
        setFolderContextMenuId(null);
      }
    };
    document.addEventListener("click", handleClickOutside);
    return () => document.removeEventListener("click", handleClickOutside);
  }, [folderContextMenuId]);

  const handleDragEnd = async (result) => {
    console.log("Drag Debug:", result);
    if (!result.destination) {
      console.log("No destination");
      return;
    }

    const sourceId = result.source.droppableId;
    const destId = result.destination.droppableId;
    const draggedId = result.draggableId;

    // Find the conversation that was dragged
    const draggedConv = conversations.find((c) => c.id === draggedId);
    if (!draggedConv) return;

    // Determine the new folder_id based on destination
    let newFolderId = null;
    if (destId === "starred" || destId === "recent") {
      newFolderId = null; // No folder
    } else if (destId.startsWith("folder-")) {
      newFolderId = destId.replace("folder-", "");
    }

    // Update the conversation's folder if it changed
    const folderChanged = draggedConv.folder_id !== newFolderId;

    if (folderChanged) {
      // Update folder_id
      try {
        const res = await authFetch(
          apiUrl(`/api/chat/conversations/${draggedId}`),
          {
            method: "PUT",
            headers: { "Content-Type": "application/json" },
            body: JSON.stringify({ folder_id: newFolderId }),
          },
        );

        if (res.ok) {
          // Update local state
          setConversations((prev) =>
            prev.map((c) =>
              c.id === draggedId ? { ...c, folder_id: newFolderId } : c,
            ),
          );
        }
      } catch (error) {
        console.error("Failed to move conversation to folder:", error);
        return;
      }
    }

    // Handle reordering within the same group
    if (sourceId === destId) {
      // Get the current group's conversations
      const groupKey =
        destId === "recent"
          ? "recent"
          : destId === "starred"
            ? "starred"
            : destId.replace("folder-", "");
      const groupConvs = groupedConversations[groupKey];

      // Simple reorder within the group
      const reorderedGroup = Array.from(groupConvs);
      const [movedItem] = reorderedGroup.splice(result.source.index, 1);
      reorderedGroup.splice(result.destination.index, 0, movedItem);

      // Update the conversations array
      const updatedConversations = conversations.map((conv) => {
        const newIndex = reorderedGroup.findIndex((gc) => gc.id === conv.id);
        if (newIndex !== -1) {
          return { ...conv, display_order: newIndex };
        }
        return conv;
      });

      setConversations(updatedConversations);

      // Save to server
      try {
        const response = await authFetch(
          apiUrl("/api/chat/conversations/reorder"),
          {
            method: "POST",
            body: JSON.stringify({
              updates: reorderedGroup.map((item, index) => ({
                id: item.id,
                display_order: index,
              })),
            }),
          },
        );

        // ✅ FIX #4: Check response and revert on failure
        if (!response.ok) {
          throw new Error("Failed to save order");
        }

        // ✅ FIX #4: Invalidate cache to force fresh data on next load
        localStorage.removeItem("conversations_cache");
      } catch (error) {
        console.error("Failed to reorder conversations:", error);
        // ✅ FIX #4: Show error and revert UI
        alert("Failed to save order. Refreshing...");
        await loadConversations(); // Reload to get correct order from server
      }
    }
  };

  // Check for uploads before collapsing sidebar
  const handleToggleCollapse = () => {
    // If collapsing (not expanding) and upload in progress, warn user
    if (!isCollapsed && window.__fileBrowserUploading) {
      const confirmed = window.confirm(
        "Upload in progress! Collapsing the sidebar will cancel it. Continue?"
      );
      if (!confirmed) return;
    }
    onToggleCollapse();
  };

  const startEdit = (conv) => {
    setEditingId(conv.id);
    setEditingTitle(conv.title);
    setContextMenuId(null);
    setTimeout(() => editInputRef.current?.select(), 0);
  };

  const renameConversation = async (conversationId, title) => {
    const newTitle = title.trim();
    if (!newTitle || !conversationId) return false;

    console.log("Saving edit:", conversationId, newTitle);

    try {
      const response = await authFetch(
        apiUrl(`/api/chat/conversations/${conversationId}/rename`),
        {
          method: "POST",
          credentials: "include",
          body: JSON.stringify({ title: newTitle }),
        },
      );

      const data = await response.json();
      if (response.ok && data.success) {
        setConversations((prev) =>
          prev.map((conversation) =>
            conversation.id === conversationId
              ? { ...conversation, title: newTitle }
              : conversation,
          ),
        );
        if (searchResults) {
          setSearchResults((prev) =>
            prev.map((conversation) =>
              conversation.id === conversationId
                ? { ...conversation, title: newTitle }
                : conversation,
            ),
          );
        }
        return true;
      }
      console.error("Rename failed:", data.error);
    } catch (error) {
      console.error("Failed to rename conversation:", error);
    }
    return false;
  };

  const saveEdit = async () => {
    if (!editingTitle.trim() || !editingId) {
      setEditingId(null);
      return;
    }

    await renameConversation(editingId, editingTitle);
    setEditingId(null);
    setEditingTitle("");
  };

  const toggleStar = async (convId, currentStarred) => {
    try {
      // Update UI immediately for better UX
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId ? { ...c, is_starred: currentStarred ? 0 : 1 } : c,
        ),
      );
      setContextMenuId(null); // Close menu

      const res = await authFetch(
        apiUrl(`/api/chat/conversations/${convId}/star`),
        {
          method: "POST",
          body: JSON.stringify({ starred: currentStarred ? false : true }),
        },
      );

      if (!res.ok) {
        // Revert on error
        setConversations((prev) =>
          prev.map((c) =>
            c.id === convId ? { ...c, is_starred: currentStarred } : c,
          ),
        );
        console.error("Failed to star conversation");
      }
    } catch (error) {
      console.error("Failed to star conversation:", error);
      // Revert on error
      setConversations((prev) =>
        prev.map((c) =>
          c.id === convId ? { ...c, is_starred: currentStarred } : c,
        ),
      );
    }
  };

  const deleteConversation = async (convId) => {
    console.log(`🗑️ [DELETE] Attempting to delete conversation: ${convId}`);

    // Immediately update UI optimistically
    const originalConversations = conversations;
    setConversations((prev) => prev.filter((c) => c.id !== convId));

    try {
      // Soft delete - archive instead of permanently delete
      const res = await authFetch(apiUrl(`/api/chat/conversations/${convId}`), {
        method: "PUT",
        body: JSON.stringify({ archived: true }),
      });

      console.log(`🗑️ [DELETE] Server response status: ${res.status}`);

      if (!res.ok) {
        // Revert UI on error
        setConversations(originalConversations);

        try {
          const errorData = await res.json();
          console.error(`🗑️ [DELETE] Server error:`, errorData);
          alert(
            `Failed to delete conversation: ${errorData.error || res.statusText}`,
          );
        } catch (e) {
          alert(`Failed to delete conversation: ${res.statusText}`);
        }
        return;
      }

      // Parse response
      let responseData = {};
      try {
        responseData = await res.json();
        console.log(`🗑️ [DELETE] Server response:`, responseData);
      } catch (e) {
        console.log(`🗑️ [DELETE] No JSON response body`);
      }

      // Success - UI already updated optimistically
      console.log(`🗑️ [DELETE] Successfully archived on server`);

      // Notify parent that conversation was deleted
      if (onConversationDeleted) {
        console.log(`🗑️ [DELETE] Notifying parent of deletion`);
        onConversationDeleted(convId);
      }

      // Don't reload - we already updated the UI optimistically
      console.log(`🗑️ [DELETE] Delete operation complete`);
    } catch (error) {
      // Revert UI on error
      setConversations(originalConversations);
      console.error("🗑️ [DELETE] Failed to archive conversation:", error);
      alert(`Error deleting conversation: ${error.message}`);
    } finally {
      setDeleteConfirmId(null);
      setContextMenuId(null);
    }
  };

  // ============ MULTI-SELECT FUNCTIONS ============

  const toggleSelectionMode = () => {
    if (selectionMode) {
      // Exiting selection mode - clear selections
      setSelectedConversations(new Set());
    }
    setSelectionMode(!selectionMode);
  };

  const toggleConversationSelection = (convId, event) => {
    event?.stopPropagation();
    setSelectedConversations((prev) => {
      const next = new Set(prev);
      if (next.has(convId)) {
        next.delete(convId);
      } else {
        next.add(convId);
      }
      return next;
    });
  };

  const selectAllConversations = () => {
    const allIds = conversations.map((c) => c.id);
    setSelectedConversations(new Set(allIds));
  };

  const clearSelection = () => {
    setSelectedConversations(new Set());
  };

  const handleBulkDelete = async () => {
    if (selectedConversations.size === 0) return;

    const confirmed = window.confirm(
      `Delete ${selectedConversations.size} conversation${selectedConversations.size > 1 ? "s" : ""}?`
    );
    if (!confirmed) return;

    setBulkDeleting(true);
    const toDelete = Array.from(selectedConversations);
    const originalConversations = conversations;

    // Optimistic UI update
    setConversations((prev) => prev.filter((c) => !selectedConversations.has(c.id)));

    try {
      // Delete all selected conversations
      const results = await Promise.all(
        toDelete.map((convId) =>
          authFetch(apiUrl(`/api/chat/conversations/${convId}`), {
            method: "PUT",
            body: JSON.stringify({ archived: true }),
          })
        )
      );

      const allSuccessful = results.every((res) => res.ok);
      if (!allSuccessful) {
        // Some failed - show error but don't revert (partial success)
        console.warn("Some conversations failed to delete");
      }

      // Clear selection and exit selection mode
      setSelectedConversations(new Set());
      setSelectionMode(false);

      // Notify parent if current conversation was deleted
      if (selectedConversations.has(currentConversationId) && onConversationDeleted) {
        onConversationDeleted(currentConversationId);
      }
    } catch (error) {
      console.error("Bulk delete failed:", error);
      setConversations(originalConversations);
      alert("Failed to delete some conversations");
    } finally {
      setBulkDeleting(false);
    }
  };

  const groupedConversations = useMemo(() => {
    // Use search results if available, otherwise use all conversations
    const source = searchResults !== null ? searchResults : conversations;

    // Create a copy and sort by starred first, then by display_order
    const sorted = [...source].sort((a, b) => {
      if (a.is_starred && !b.is_starred) return -1;
      if (!a.is_starred && b.is_starred) return 1;
      return (a.display_order || 0) - (b.display_order || 0);
    });

    const groups = {
      starred: sorted.filter((c) => c.is_starred),
      recent: sorted.filter((c) => !c.is_starred && !c.folder_id),
    };

    folders.forEach((folder) => {
      groups[folder.id] = sorted.filter(
        (c) => !c.is_starred && c.folder_id === folder.id,
      );
    });

    return groups;
  }, [conversations, folders, searchResults]);

  const formatDate = (dateStr) => {
    const date = new Date(dateStr);
    if (!dateStr || Number.isNaN(date.getTime())) return "";
    const now = new Date();
    const diffHours = (now - date) / (1000 * 60 * 60);

    if (diffHours < 1) return "Now";
    if (diffHours < 24) return `${Math.floor(diffHours)}h`;
    if (diffHours < 168) return `${Math.floor(diffHours / 24)}d`;
    return date.toLocaleDateString("en-US", { month: "short", day: "numeric" });
  };

  const commandGroups = useMemo(() => {
    const toCommandConversation = (conversation) => ({
      id: conversation.id,
      title: conversation.title,
      isStarred:
        conversation.is_starred === 1 || conversation.is_starred === true,
      updatedAtLabel: formatDate(conversation.updated_at),
      source: conversation,
    });

    if (searchResults !== null) {
      return [
        {
          id: "matches",
          label: "Matches",
          conversations: searchResults.map(toCommandConversation),
          collapsible: false,
        },
      ];
    }

    return [
      {
        id: "starred",
        label: "Starred",
        conversations: groupedConversations.starred.map(toCommandConversation),
        initiallyExpanded: true,
      },
      ...folders.map((folder) => ({
        id: `folder-${folder.id}`,
        label: folder.name,
        conversations: (groupedConversations[folder.id] || []).map(
          toCommandConversation,
        ),
        initiallyExpanded: true,
      })),
      {
        id: "recent",
        label: "Recent",
        conversations: groupedConversations.recent.map(toCommandConversation),
        collapsible: false,
      },
    ];
  }, [folders, groupedConversations, searchResults]);

  const renderConversationItem = (conv, index, isDragging) => (
    <div
      className={`conversation-item ${conv.id === currentConversationId ? "active" : ""} ${isDragging ? "dragging" : ""} ${conv.is_starred ? "has-star" : ""} ${selectedConversations.has(conv.id) ? "selected" : ""}`}
      onClick={(e) => {
        if (selectionMode) {
          toggleConversationSelection(conv.id, e);
        } else {
          onSelectConversation(conv.id);
        }
      }}
    >
      {selectionMode ? (
        <button
          className="selection-checkbox"
          onClick={(e) => toggleConversationSelection(conv.id, e)}
        >
          {selectedConversations.has(conv.id) ? (
            <CheckSquare size={16} className="checkbox-checked" />
          ) : (
            <Square size={16} className="checkbox-unchecked" />
          )}
        </button>
      ) : (conv.is_starred === 1 || conv.is_starred === true) && (
        <Star size={14} className="star-icon starred" fill="#ffcc00" />
      )}
      {editingId === conv.id ? (
        <input
          ref={editInputRef}
          type="text"
          value={editingTitle}
          onChange={(e) => setEditingTitle(e.target.value)}
          onBlur={saveEdit}
          onKeyDown={(e) => {
            if (e.key === "Enter") {
              e.preventDefault();
              saveEdit();
            }
            if (e.key === "Escape") {
              e.preventDefault();
              setEditingId(null);
              setEditingTitle("");
            }
          }}
          className="edit-input"
          onClick={(e) => e.stopPropagation()}
        />
      ) : (
        <span className="conversation-title">
          {(() => {
            const b = surfaceBadge(conv.last_surface);
            return b ? (
              <img
                className="conversation-surface-badge"
                src={b.img}
                title={`Last touched in ${b.label}`}
                alt={b.label}
                width="16"
                height="16"
                style={{ marginRight: 6, borderRadius: 4, verticalAlign: "text-bottom" }}
              />
            ) : null;
          })()}
          {conv.title}
          {conv.team_id && (
            <span
              title="Shared with your team"
              style={{ marginLeft: 6, fontSize: 11, opacity: 0.8 }}
            >
              👥
            </span>
          )}
        </span>
      )}

      <span className="conversation-time">{formatDate(conv.updated_at)}</span>

      {deleteConfirmId === conv.id ? (
        <div className="delete-confirm-overlay">
          <span className="delete-text">Delete?</span>
          <button
            onClick={(e) => {
              e.stopPropagation();
              deleteConversation(conv.id);
            }}
            className="action-btn confirm-yes"
            title="Yes, delete"
          >
            <Check size={14} />
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmId(null);
            }}
            className="action-btn confirm-no"
            title="Cancel"
          >
            <X size={14} />
          </button>
        </div>
      ) : (
        <div className="conversation-actions">
          <button
            onClick={(e) => {
              e.stopPropagation();
              setContextMenuId(contextMenuId === conv.id ? null : conv.id);
            }}
            className="action-btn"
          >
            <MoreVertical size={14} />
          </button>
        </div>
      )}

      {contextMenuId === conv.id && (
        <div className="context-menu">
          <button
            onClick={(e) => {
              e.stopPropagation();
              toggleStar(
                conv.id,
                conv.is_starred === 1 || conv.is_starred === true,
              );
            }}
          >
            <Star size={14} />
            {conv.is_starred === 1 || conv.is_starred === true
              ? "Unstar"
              : "Star"}
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              startEdit(conv);
            }}
          >
            <Edit2 size={14} />
            Rename
          </button>
          <button
            onClick={(e) => {
              e.stopPropagation();
              setDeleteConfirmId(conv.id);
              setContextMenuId(null);
            }}
            className="danger"
          >
            <Trash2 size={14} />
            Delete
          </button>
        </div>
      )}
    </div>
  );

  // Render folder modal
  const renderFolderModal = () => {
    if (!showFolderModal) return null;

    return (
      <>
        <div className="folder-modal-backdrop" onClick={closeFolderModal} />
        <div className="folder-modal" onClick={(e) => e.stopPropagation()}>
          <h3 className="folder-modal-title">
            {editingFolder ? "Edit Folder" : "Create New Folder"}
          </h3>

          <div className="folder-modal-field">
            <label>Folder Name</label>
            <input
              ref={folderNameInputRef}
              type="text"
              value={folderName}
              onChange={(e) => setFolderName(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === "Enter") saveFolder();
                if (e.key === "Escape") closeFolderModal();
              }}
              placeholder="Enter folder name..."
              autoFocus
            />
          </div>

          <div className="folder-modal-field">
            <label>
              <Palette size={12} style={{ marginRight: "4px" }} />
              Folder Color
            </label>
            <div className="folder-color-picker">
              {FOLDER_COLORS.map((color) => (
                <button
                  key={color}
                  type="button"
                  className={`folder-color-btn ${folderColor === color ? "selected" : ""}`}
                  style={{ background: color }}
                  onClick={() => setFolderColor(color)}
                />
              ))}
            </div>
          </div>

          <div className="folder-modal-actions">
            <button
              type="button"
              className="folder-modal-cancel"
              onClick={closeFolderModal}
            >
              Cancel
            </button>
            <button
              type="button"
              className="folder-modal-submit"
              style={{ background: folderColor }}
              onClick={saveFolder}
            >
              {editingFolder ? "Save Changes" : "Create Folder"}
            </button>
          </div>
        </div>
      </>
    );
  };

  if (
    commandCenterChatEnabled &&
    sidebarMode === "chats" &&
    !commandManagementOpen
  ) {
    return (
      <CommandConversationNav
        currentConversationId={currentConversationId}
        groups={commandGroups}
        searchQuery={searchQuery}
        onSearchChange={setSearchQuery}
        isLoading={isLoadingConversations}
        error={conversationError}
        onRetry={() =>
          searchQuery.trim()
            ? runConversationSearch(searchQuery)
            : loadConversations()
        }
        onSelectConversation={onSelectConversation}
        onNewConversation={onNewConversation}
        onClose={onToggleCollapse}
        isOpen={!isCollapsed}
        isModal={window.innerWidth <= 820}
        onManageConversations={() => setCommandManagementOpen(true)}
        onRenameConversation={async (conversation) => {
          const nextTitle = window.prompt(
            "Rename command",
            conversation.title || "",
          );
          if (nextTitle !== null) {
            await renameConversation(conversation.id, nextTitle);
          }
        }}
        onToggleStar={(conversation) =>
          toggleStar(conversation.id, conversation.isStarred)
        }
        onDeleteConversation={(conversation) => {
          if (
            window.confirm(
              `Delete “${conversation.title || "Untitled command"}”?`,
            )
          ) {
            deleteConversation(conversation.id);
          }
        }}
      />
    );
  }

  return (
    <div className={`conversation-sidebar ${isCollapsed ? "collapsed" : ""}`}>
      {/* Folder Modal */}
      {renderFolderModal()}

      {/* Chats mode header */}
      {sidebarMode === "chats" && (
        <div className="sidebar-header">
          {commandCenterChatEnabled && commandManagementOpen && (
            <button
              type="button"
              className="selection-action-btn"
              onClick={() => setCommandManagementOpen(false)}
            >
              Done
            </button>
          )}
          {selectionMode ? (
            <>
              {/* Selection mode header */}
              <button
                className="selection-cancel-btn"
                onClick={toggleSelectionMode}
                title="Cancel selection"
              >
                <XCircle size={18} />
              </button>
              <span className="selection-count">
                {selectedConversations.size} selected
              </span>
              <button
                className="selection-action-btn"
                onClick={selectAllConversations}
                title="Select all"
              >
                Select All
              </button>
              <button
                className="selection-action-btn danger"
                onClick={handleBulkDelete}
                disabled={selectedConversations.size === 0 || bulkDeleting}
                title="Delete selected"
              >
                <Trash2 size={14} />
                {bulkDeleting ? "..." : "Delete"}
              </button>
            </>
          ) : (
            <>
              {/* Normal mode header */}
              <button
                className="door-toggle-btn"
                onClick={handleToggleCollapse}
                title={isCollapsed ? "Open Sidebar" : "Close Sidebar"}
              >
                {isCollapsed ? <DoorOpen size={20} /> : <DoorClosed size={20} />}
              </button>
              <button className="new-chat-btn hf-lift hf-press hf-focus" onClick={onNewConversation}>
                <Plus size={18} />
                <span>New Chat</span>
              </button>
              <button
                className="new-folder-btn"
                onClick={openCreateFolderModal}
                title="Create new folder"
              >
                <FolderPlus size={16} />
              </button>
              <button
                className="selection-mode-btn"
                onClick={toggleSelectionMode}
                title="Select multiple conversations"
              >
                <CheckSquare size={16} />
              </button>
            </>
          )}
        </div>
      )}

      {/* Files mode - FileBrowser */}
      {sidebarMode === "files" && !isCollapsed && (
        <FileBrowser
          userId={user?.id}
          isCollapsed={isCollapsed}
          onUseInChat={onFileUseInChat}
        />
      )}

      {!isCollapsed && sidebarMode === "chats" && (
        <>
          <div className="sidebar-search">
            <Search size={16} />
            <input
              type="text"
              placeholder="Search Conversations"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              className="search-input"
            />
          </div>

          <DragDropContext onDragEnd={handleDragEnd}>
            <div className="sidebar-conversations">
              {/* Starred conversations */}
              {groupedConversations.starred.length > 0 && (
                <div className="conversation-group">
                  <div className="group-label">
                    <Star size={12} />
                    <span>STARRED</span>
                  </div>
                  <Droppable droppableId="starred">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="hf-stagger">
                        {groupedConversations.starred.map((conv, index) => (
                          <Draggable
                            key={conv.id}
                            draggableId={conv.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {renderConversationItem(
                                  conv,
                                  index,
                                  snapshot.isDragging,
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              )}

              {/* Folder groups - shown after Starred, before Recent */}
              {folders.map((folder) => {
                const folderConvs = groupedConversations[folder.id] || [];
                // Show empty folders too so users can drag into them
                const isExpanded = expandedFolders.has(folder.id);

                return (
                  <div
                    key={folder.id}
                    className="conversation-group folder-group"
                  >
                    <div
                      className="group-label folder-label"
                      style={{ position: "relative" }}
                    >
                      <div
                        style={{
                          display: "flex",
                          alignItems: "center",
                          flex: 1,
                          cursor: "pointer",
                        }}
                        onClick={() => {
                          setExpandedFolders((prev) => {
                            const next = new Set(prev);
                            if (next.has(folder.id)) {
                              next.delete(folder.id);
                            } else {
                              next.add(folder.id);
                            }
                            return next;
                          });
                        }}
                      >
                        <FolderOpen
                          size={12}
                          style={{ color: folder.color || "#007AFF" }}
                        />
                        <span style={{ marginLeft: "6px" }}>
                          {folder.name.toUpperCase()}
                        </span>
                        <span className="group-count">
                          {folderConvs.length}
                        </span>
                      </div>

                      {/* Folder actions button */}
                      <button
                        className="folder-menu-btn"
                        onClick={(e) => {
                          e.stopPropagation();
                          setFolderContextMenuId(
                            folderContextMenuId === folder.id
                              ? null
                              : folder.id,
                          );
                        }}
                        style={{
                          background: "transparent",
                          border: "none",
                          padding: "2px 4px",
                          cursor: "pointer",
                          color: "var(--text-tertiary)",
                          borderRadius: "4px",
                          opacity: 0.6,
                          transition: "opacity 0.2s",
                        }}
                        onMouseEnter={(e) => (e.target.style.opacity = 1)}
                        onMouseLeave={(e) => (e.target.style.opacity = 0.6)}
                      >
                        <MoreVertical size={12} />
                      </button>

                      {/* Folder context menu */}
                      {folderContextMenuId === folder.id && (
                        <div
                          className="folder-context-menu"
                          style={{
                            position: "absolute",
                            right: 0,
                            top: "100%",
                            background: "var(--bg-tertiary)",
                            border: "1px solid var(--border-primary)",
                            borderRadius: "8px",
                            boxShadow: "var(--hf-shadow-2)",
                            zIndex: 1000,
                            minWidth: "140px",
                            overflow: "hidden",
                          }}
                        >
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              openEditFolderModal(folder);
                            }}
                            style={{
                              display: "flex",
                              alignItems: "center",
                              gap: "8px",
                              width: "100%",
                              padding: "10px 14px",
                              background: "transparent",
                              border: "none",
                              color: "var(--text-primary)",
                              fontSize: "13px",
                              cursor: "pointer",
                              textAlign: "left",
                            }}
                            onMouseEnter={(e) =>
                              (e.target.style.background = "var(--bg-hover)")
                            }
                            onMouseLeave={(e) =>
                              (e.target.style.background = "transparent")
                            }
                          >
                            <Edit2 size={14} />
                            Edit Folder
                          </button>
                          {deleteFolderConfirmId === folder.id ? (
                            <div
                              style={{
                                display: "flex",
                                alignItems: "center",
                                padding: "8px 14px",
                                gap: "8px",
                              }}
                            >
                              <span
                                style={{ color: "var(--accent-danger)", fontSize: "12px" }}
                              >
                                Delete?
                              </span>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  deleteFolder(folder.id);
                                }}
                                style={{
                                  padding: "4px 8px",
                                  background: "var(--accent-danger)",
                                  border: "none",
                                  borderRadius: "4px",
                                  color: "var(--text-on-accent)",
                                  fontSize: "11px",
                                  cursor: "pointer",
                                }}
                              >
                                Yes
                              </button>
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  setDeleteFolderConfirmId(null);
                                }}
                                style={{
                                  padding: "4px 8px",
                                  background: "var(--bg-tertiary)",
                                  border: "none",
                                  borderRadius: "4px",
                                  color: "var(--text-primary)",
                                  fontSize: "11px",
                                  cursor: "pointer",
                                }}
                              >
                                No
                              </button>
                            </div>
                          ) : (
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                setDeleteFolderConfirmId(folder.id);
                              }}
                              style={{
                                display: "flex",
                                alignItems: "center",
                                gap: "8px",
                                width: "100%",
                                padding: "10px 14px",
                                background: "transparent",
                                border: "none",
                                color: "var(--accent-danger)",
                                fontSize: "13px",
                                cursor: "pointer",
                                textAlign: "left",
                              }}
                              onMouseEnter={(e) =>
                                (e.target.style.background = "var(--bg-hover)")
                              }
                              onMouseLeave={(e) =>
                                (e.target.style.background = "transparent")
                              }
                            >
                              <Trash2 size={14} />
                              Delete Folder
                            </button>
                          )}
                        </div>
                      )}
                    </div>

                    {/* Folder contents - always render Droppable for drag target */}
                    <Droppable droppableId={`folder-${folder.id}`}>
                      {(provided, snapshot) => (
                        <div
                          {...provided.droppableProps}
                          ref={provided.innerRef}
                          className="hf-stagger"
                          style={{
                            minHeight: snapshot.isDraggingOver ? "40px" : "0px",
                            background: snapshot.isDraggingOver
                              ? `${folder.color || "#007AFF"}15`
                              : "transparent",
                            borderRadius: "6px",
                            transition: "all 0.2s",
                            display:
                              isExpanded || snapshot.isDraggingOver
                                ? "block"
                                : "none",
                          }}
                        >
                          {folderConvs.length === 0 &&
                            !snapshot.isDraggingOver ? (
                            <div
                              style={{
                                padding: "8px 12px",
                                color: "var(--text-tertiary)",
                                fontSize: "12px",
                                fontStyle: "italic",
                              }}
                            >
                              Drag conversations here
                            </div>
                          ) : (
                            folderConvs.map((conv, index) => (
                              <Draggable
                                key={conv.id}
                                draggableId={conv.id}
                                index={index}
                              >
                                {(provided, snapshot) => (
                                  <div
                                    ref={provided.innerRef}
                                    {...provided.draggableProps}
                                    {...provided.dragHandleProps}
                                  >
                                    {renderConversationItem(
                                      conv,
                                      index,
                                      snapshot.isDragging,
                                    )}
                                  </div>
                                )}
                              </Draggable>
                            ))
                          )}
                          {provided.placeholder}
                        </div>
                      )}
                    </Droppable>
                  </div>
                );
              })}

              {/* Recent conversations */}
              {groupedConversations.recent.length > 0 && (
                <div className="conversation-group">
                  <div className="group-label">RECENT</div>
                  <Droppable droppableId="recent">
                    {(provided) => (
                      <div {...provided.droppableProps} ref={provided.innerRef} className="hf-stagger">
                        {groupedConversations.recent.map((conv, index) => (
                          <Draggable
                            key={conv.id}
                            draggableId={conv.id}
                            index={index}
                          >
                            {(provided, snapshot) => (
                              <div
                                ref={provided.innerRef}
                                {...provided.draggableProps}
                                {...provided.dragHandleProps}
                              >
                                {renderConversationItem(
                                  conv,
                                  index,
                                  snapshot.isDragging,
                                )}
                              </div>
                            )}
                          </Draggable>
                        ))}
                        {provided.placeholder}
                      </div>
                    )}
                  </Droppable>
                </div>
              )}
            </div>
          </DragDropContext>
        </>
      )}

      {/* Footer - ALWAYS visible when not collapsed (in both Chats and Files mode) */}
      {!isCollapsed && (
        <div className="sidebar-footer">
          {/* InstallPrompt retired 2026-09-01: the install nudge now lives in
              the chat flow (InstallNudge, after ~5 messages per Brad) — one
              surface, earn-it timing, dismiss remembered. */}
          <a
            href="https://optimus.university"
            target="_blank"
            rel="noreferrer"
            className="sidebar-footer-row"
            style={{ textDecoration: "none", color: "var(--text-secondary)", fontSize: 13, display: "flex", alignItems: "center", gap: 6, padding: "6px 8px" }}
          >
            🎓 Learning — Optimus University
          </a>

          {/* Settings row: Settings button + Mode toggle */}
          <div className="sidebar-footer-row">
            <button className="settings-btn desktop-only" onClick={onOpenSettings}>
              <Settings size={16} />
              <span>Settings</span>
            </button>
            <SidebarModeToggle
              mode={sidebarMode}
              onModeChange={onSidebarModeChange}
            />
          </div>

          {/* Mobile User Profile Section */}
          <div className="mobile-user-profile">
            <div
              className={`mobile-user-info ${showMobileUserMenu ? 'active' : ''}`}
              onClick={() => setShowMobileUserMenu(!showMobileUserMenu)}
              style={{ cursor: 'pointer' }}
            >
              {user?.imageUrl ? (
                <img src={user.imageUrl} alt={user.name} className="mobile-avatar" />
              ) : (
                <div className="mobile-avatar-placeholder">
                  {user?.name?.charAt(0) || "U"}
                </div>
              )}
              <div className="mobile-user-details">
                <span className="mobile-user-name">{user?.name}</span>
                <span className="mobile-user-email">{user?.email}</span>
              </div>
              <div style={{ marginLeft: 'auto', opacity: 0.5 }}>
                {showMobileUserMenu ? <ChevronLeft size={16} style={{ transform: 'rotate(-90deg)' }} /> : <ChevronLeft size={16} style={{ transform: 'rotate(90deg)' }} />}
              </div>
            </div>

            {showMobileUserMenu && (
              <div className="mobile-user-actions" style={{ animation: 'fadeIn 0.2s ease' }}>
                <button className="mobile-action-btn" onClick={onOpenSettings}>
                  <Settings size={16} />
                  <span>Settings</span>
                </button>
                <button className="mobile-action-btn" onClick={() => openUserProfile()}>
                  <div className="icon-wrapper">👤</div>
                  <span>Account</span>
                </button>
                <button className="mobile-action-btn logout" onClick={onLogout}>
                  <LogOut size={16} />
                  <span>Log Out</span>
                </button>
              </div>
            )}
          </div>
        </div>
      )}
    </div>
  );
};

export default ConversationSidebar;
